/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useEffect, useState } from 'react';
import {
  RouteComponentProps,
  Route,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { useAuthContext } from '@asgardeo/auth-react';
import logger from 'utils/logger';
import { useUser } from 'contexts/User';
import PageLoader from 'components/PreLoader/PageLoader';
import { ERROR_VISIT, LOGIN, SIGNIN } from 'routes';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import {
  AuthSessionVariable,
  getSessionItem,
  setSessionItem,
  SignInConfig,
} from 'utils/auth';
import { CloudType } from 'data/api/interfaces';
import DirectVisitWarning from 'pages/DirectVisitWarning';

export interface ProtectedRouteProps extends RouteComponentProps {
  children: JSX.Element;
  path: string | string[];
}

export default function ProtectedRoute(props: Partial<ProtectedRouteProps>) {
  const { children, ...rest } = props;
  const history = useHistory();
  const location: Location = useLocation<Location>();
  const selectedFidp = getSessionItem(AuthSessionVariable.FIDP);
  const params = new URLSearchParams(location.search);
  const fidpQueryParam = params.get('fidp') || '';
  const orgIdQueryParam = params.get('orgId') || '';
  const regionQueryParam = params.get('region') || '';
  const usernameQueryParam = params.get('username') || '';

  const { state, signIn } = useAuthContext();
  const [, setOrganization] = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const [cloudType, setCloudType] = useState<CloudType>(CloudType.Asgardeo);
  const [isAsgardeoOrgIdMissing, setIsAsgardeoOrgIdMissing] =
    useState<boolean>(false);

  useEffect(() => {
    const path: string = location.pathname;

    if (
      path.includes(CloudType.Asgardeo) ||
      path.includes(CloudType.Choreo) ||
      path.includes(CloudType.Bijira) ||
      path.includes(CloudType.Devant)
    ) {
      setSessionItem(AuthSessionVariable.INIT_URL, path);
    }

    const pathStr: string = getSessionItem(AuthSessionVariable.INIT_URL) || '';

    const cloudTypeValue = Object.values(CloudType).find((cloudTypeKey) =>
      pathStr.includes(cloudTypeKey)
    );

    setCloudType(cloudTypeValue as CloudType);
    setSessionItem(AuthSessionVariable.CLOUD_TYPE, cloudTypeValue as CloudType);
  }, [location]);

  useEffect(() => {
    if (orgIdQueryParam !== '') {
      setSessionItem(AuthSessionVariable.ORG_UUID, orgIdQueryParam);
    }
  }, [orgIdQueryParam]);

  useEffect(() => {
    if (regionQueryParam !== '') {
      setSessionItem(AuthSessionVariable.REGION, regionQueryParam);
    }
  }, [regionQueryParam]);

  useEffect(() => {
    const redirectionUrl = location.pathname;

    if (!redirectionUrl.includes(SIGNIN) && !redirectionUrl.includes(LOGIN)) {
      setSessionItem(AuthSessionVariable.RETURN_TO_URL, redirectionUrl);
    }

    if (cloudType) {
      if (!state.isAuthenticated) {
        if (
          !orgIdQueryParam &&
          !getSessionItem(AuthSessionVariable.ORG_UUID) &&
          cloudType === CloudType.Asgardeo
        ) {
          setIsAsgardeoOrgIdMissing(true);
        }

        if (fidpQueryParam && !selectedFidp) {
          setSessionItem(AuthSessionVariable.FIDP, fidpQueryParam);
        }

        const config: SignInConfig = {
          fidp: selectedFidp || fidpQueryParam,
        };
        if (!isAsgardeoOrgIdMissing && !state.isLoading) {
          if (usernameQueryParam !== '') {
            config.username = usernameQueryParam;
          }
          signIn(config).catch((signInError) => {
            logger.error(signInError);

            enqueueSnackbar(
              intl.formatMessage({
                id: 'protectedRoute.login.failure.message',
                defaultMessage: 'Failed to log in',
              }),
              {
                variant: 'error',
              }
            );
          });
        }
      } else if (state.isAuthenticated) {
        const orgUuid = getSessionItem(AuthSessionVariable.ORG_UUID);
        if (orgUuid) {
          const defaultOrg = {
            handle: '',
            id: 0,
            uuid: orgUuid,
            name: '',
          };
          setOrganization({
            selectedOrg: defaultOrg,
          });
          const returnToUrl =
            getSessionItem(AuthSessionVariable.RETURN_TO_URL) || '/';
          history.push(returnToUrl.split('?orgId')[0]);
        } else {
          history.push(ERROR_VISIT);
        }
      }
    }
  }, [
    cloudType,
    state.isAuthenticated,
    state.isLoading,
    fidpQueryParam,
    selectedFidp,
  ]);

  if (isAsgardeoOrgIdMissing) {
    return <DirectVisitWarning />;
  }

  if (!state.isAuthenticated) {
    return <PageLoader />;
  }

  return (
    <Route
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      render={() => children}
    />
  );
}
